import React, { useCallback } from 'react';
import { defineStyles, useStyles } from '@/components/hooks/useStyles';
import HoverOver from '@/components/common/HoverOver';
import { useDialog } from '@/components/common/withDialog';
import LLMScoreDialog from '../LLMScoreDialog';
import classNames from 'classnames';

const styles = defineStyles('LLMScoreBadge', (theme: ThemeType) => ({
  badge: {
    ...theme.typography.commentStyle,
    fontSize: 11,
    padding: '2px 6px',
    borderRadius: 3,
    cursor: 'pointer',
    transition: 'background-color 0.1s ease',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[700],
    flexShrink: 0,
    '&:hover': {
      backgroundColor: theme.palette.grey[300],
    },
  },
  highScore: {
    color: theme.palette.error.main,
    backgroundColor: 'light-dark(rgba(191,54,12,0.1),rgba(191,54,12,0.25))',
    '&:hover': {
      backgroundColor: 'light-dark(rgba(191,54,12,0.18),rgba(191,54,12,0.35))',
    },
  },
}));

const HIGH_SCORE_THRESHOLD = 0.2;

const LLMScoreBadge = ({
  documentId,
  automatedContentEvaluations,
  contentHtml,
  contentType,
  showWhenEmpty = false,
  stopPropagation = false,
  onLlmCheckComplete,
  className,
}: {
  documentId: string;
  automatedContentEvaluations: AutomatedContentEvaluationsFragment | null | undefined;
  contentHtml: string;
  contentType: 'Post' | 'Comment';
  showWhenEmpty?: boolean;
  stopPropagation?: boolean;
  onLlmCheckComplete?: () => void;
  className?: string;
}) => {
  const classes = useStyles(styles);
  const { openDialog } = useDialog();

  const apiVersion = automatedContentEvaluations?.pangramApiVersion;
  const hasFractionScores = automatedContentEvaluations?.pangramApiVersion === 'v3'
    || automatedContentEvaluations?.pangramApiVersion === 'pangram-4';
  const score = automatedContentEvaluations?.pangramScore;
  const maxScore = automatedContentEvaluations?.pangramMaxScore;
  const hasScore = typeof score === 'number';

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
    openDialog({
      name: 'LLMScoreDialog',
      contents: ({ onClose }) => (
        <LLMScoreDialog
          onClose={onClose}
          documentId={documentId}
          automatedContentEvaluations={automatedContentEvaluations ?? null}
          contentHtml={contentHtml}
          contentType={contentType}
          onLlmCheckComplete={onLlmCheckComplete}
        />
      ),
    });
  }, [automatedContentEvaluations, documentId, contentHtml, contentType, stopPropagation, onLlmCheckComplete, openDialog]);

  if (!hasScore && !showWhenEmpty) return null;

  return (
    <HoverOver
      title={hasScore
        ? hasFractionScores
          ? (
            <div>
              Model: {apiVersion}, AI-involved: {score.toFixed(2)}, AI-written: {automatedContentEvaluations.pangramFractionAi?.toFixed(2) ?? 'N/A'},
              {' '}AI-assisted: {automatedContentEvaluations.pangramFractionAiAssisted?.toFixed(2) ?? 'N/A'},
              {' '}Max window: {maxScore?.toFixed(2) ?? 'N/A'}
            </div>
          )
          : <div>Average: {score.toFixed(2)}, Max: {maxScore?.toFixed(2) ?? 'N/A'}</div>
        : <div>No LLM evaluation — click to run check</div>}
    >
      <span className={classNames(classes.badge, hasScore && score > HIGH_SCORE_THRESHOLD && classes.highScore, className)} onClick={handleClick}>
        LLM: {hasScore ? score.toFixed(2) : '—'}
      </span>
    </HoverOver>
  );
};

export default LLMScoreBadge;
